import { AxiosRequestConfig } from 'axios'
import {
  ActionInput,
  Emission,
  EmissionQuery,
  EnergyConsumption,
  EnergyConsumptionQuery,
  EnergyCost,
  EnergyCostQuery,
  Sensor,
  SensorQuery,
  StatusOutput,
  SensorError,
  RuuviTag,
  DeviceMessage,
  MidSensor,
  MidSensorQuery,
  MidSensorAggregateQuery,
  MidSensorAggregate,
  SensorMetaQuery,
  SensorMeta
} from '../types'
import { Device } from '../types'
import createAPIClient from '../services/apiClient'
import { OnomondoSim } from '../types/Onomondo'
import { RebootMessage } from '../types'
import { AlertGroup, AlertTemplate } from '../types/Alerts'
import { DeviceType } from '../enums'

/**
 * The axios client for the API version 2.
 */
const api = createAPIClient('v2')

export async function getDevices(config?: AxiosRequestConfig): Promise<Device[]> {
  const response = await api.get('/devices', config)

  return response.data
}

// TODO: GET /projects/{projectId}/devices or GET /devices?projectId={projectId}
export async function getProjectDevices(
  id: number,
  config?: AxiosRequestConfig
): Promise<Device[]> {
  const response = await api.get(`/projects/${id}/devices`, config)

  return response.data
}
/**
 * Get a device by name.
 *
 * @param name - The name of the device
 * @param config - The axios request configuration
 *
 * @returns The device
 */
export async function getDeviceByName(
  name: string,
  { signal }: AxiosRequestConfig = {}
): Promise<Device> {
  const response = await api.get(`/devices/${name}/ByName`, {
    signal
  })

  return response.data
}

export async function getDevice(
  idOrName: string | number,
  config?: AxiosRequestConfig
): Promise<Device> {
  const response = await api.get(`/devices/${idOrName}`, config)

  return response.data
}

type UpdateDeviceInput = {
  type?: DeviceType
  powerSupply?: number | null
  status?: boolean
  attachedRuuviTag?: string | null
}

export async function updateDevice(
  name: string,
  input: UpdateDeviceInput,
  config?: AxiosRequestConfig
): Promise<Device> {
  const response = await api.patch(`/devices/${name}`, input, config)

  return response.data
}

export async function replaceDevice(replaceable: string, replacement: string): Promise<void> {
  await api.patch(`/devices/${replaceable}/replace/${replacement}`)
}

export async function getSensors(
  deviceCode: string,
  { from, to }: SensorQuery,
  config: AxiosRequestConfig = {}
): Promise<Sensor[]> {
  const response = await api.get(`/devices/${deviceCode}/sensors`, {
    ...config,
    params: {
      from: from.toISOString(),
      to: to.toISOString()
    }
  })

  return response.data
}

/**
 * Get sensors of the last hour.
 *
 * @param deviceCode - The device code
 * @param config - The axios request configuration
 *
 * @returns The sensors of the last hour
 */
export async function getSensorsOfTheLastHour(
  deviceCode: string,
  config?: AxiosRequestConfig
): Promise<Sensor[]> {
  const response = await api.get(`/devices/${deviceCode}/sensors/last-hour`, config)

  return response.data
}

export async function getSensorNow(
  deviceCode: string,
  config?: AxiosRequestConfig
): Promise<Sensor> {
  const response = await api.get(`/devices/${deviceCode}/sensor/now`, config)

  return response.data
}

export async function getLastSensor(
  deviceCode: string,
  config?: AxiosRequestConfig
): Promise<Sensor> {
  const response = await api.get(`/devices/${deviceCode}/sensor/last`, config)

  return response.data
}

/**
 * Get the energy consumptions of the device.
 */
export async function getEnergyConsumptions(
  deviceCode: string,
  query: EnergyConsumptionQuery,
  config: AxiosRequestConfig = {}
): Promise<EnergyConsumption[]> {
  const response = await api.get(`/devices/${deviceCode}/energy-consumptions`, {
    params: query,
    ...config
  })

  return response.data
}

/**
 * Get the energy costs of the device.
 */
export async function getEnergyCosts(
  deviceCode: string,
  query: EnergyCostQuery,
  config: AxiosRequestConfig = {}
): Promise<EnergyCost[]> {
  const response = await api.get(`/devices/${deviceCode}/energy-costs`, {
    params: query,
    ...config
  })

  return response.data
}

/**
 * Get the energy emissions of the device.
 */
export async function getEnergyEmissions(
  deviceCode: string,
  query: EmissionQuery,
  config: AxiosRequestConfig = {}
): Promise<Emission[]> {
  const response = await api.get(`/devices/${deviceCode}/energy-emissions`, {
    params: query,
    ...config
  })

  return response.data
}

/**
 * Invoke an action on a device.
 * @todo Response type!
 */
export async function invokeAction(
  name: string,
  input: ActionInput,
  config?: AxiosRequestConfig
): Promise<string> {
  const response = await api.post(`/devices/${name}/action`, input, config)

  return response.data
}

/**
 * Get the status of a device.
 */
export async function getStatus(name: string, config?: AxiosRequestConfig): Promise<StatusOutput> {
  const response = await api.get(`/devices/${name}/status`, config)

  return response.data
}

/**
 * Get the sensor errors of a device.
 */
export async function getSensorErrors(
  name: string,
  config?: AxiosRequestConfig
): Promise<SensorError[]> {
  const response = await api.get(`/devices/${name}/sensor-errors`, config)

  return response.data
}

/**
 * Get the latest sensor error of a device.
 * @param name - name of the device.
 * @returns the latest sensor error in a promise.
 * @throws if the request fails.
 */
export async function getLatestSensorError(name: string): Promise<SensorError> {
  const api = createAPIClient('v2')

  const response = await api.get(`/devices/${name}/latest-sensor-error`)
  return response.data
}

/**
 * Get the sim status of a device.
 */
export async function getSimStatus(
  name: string,
  config?: AxiosRequestConfig
): Promise<OnomondoSim> {
  const response = await api.get(`/devices/${name}/sim-status`, config)

  return response.data
}

/**
 * Fet the latest sensor reboot message.
 *
 * @param name - the name of the device.
 * @returns the latest sensor reboot message if found.
 *  @throws if the request fails.
 */
export async function getLatestReboot(name: string): Promise<RebootMessage> {
  const response = await api.get(`/devices/${name}/latest-reboot`)

  return response.data
}

/**
 * Get the paired ruuvi tag.
 */
export async function getPairedRuuviTag(
  name: string,
  config?: AxiosRequestConfig
): Promise<RuuviTag> {
  const response = await api.get(`/devices/${name}/paired-ruuvi-tag`, config)

  // Expect backend to return standardized shape; no normalization on frontend.
  return response.data
}

/**
 * Get all paired ruuvi tags discovered during ongoing pairing.
 * Returns an array (possibly empty). Uses new backend endpoint if available.
 */
export async function getPairedRuuviTags(
  name: string,
  config?: AxiosRequestConfig
): Promise<RuuviTag[]> {
  const response = await api.get(`/devices/${name}/paired-ruuvi-tags`, config)

  return response.data
}

export async function getDeviceAlertTemplates(
  config?: AxiosRequestConfig
): Promise<AlertTemplate[]> {
  const response = await api.get('/devices/alertrules/templates', config)

  return response.data
}

export async function getDeviceAlertRules(
  id: string,
  config?: AxiosRequestConfig
): Promise<AlertGroup[]> {
  const response = await api.get(`/devices/alertrules/${id}`, config)

  return response.data
}

export async function updateDeviceAlertRules(
  id: string,
  input: AlertGroup[],
  config?: AxiosRequestConfig
): Promise<AlertGroup[]> {
  const response = await api.put(`/devices/alertrules/${id}`, input, config)

  return response.data
}

type DeviceConfig = {
  report_interval: number
  ota_interval: number
  dfu: number
  u_rms: number
  max_current: number
  clamp_ratio: number
  input_cables: number
  energy_counte?: number
}

/**
 * Read the the device configuration.
 */
export async function readDeviceConfig(
  id: string,
  config?: AxiosRequestConfig
): Promise<DeviceConfig> {
  const response = await api.get(`/devices/${id}/config`, config)

  return response.data
}

type DeviceConfigInput = {
  dfu?: number
  u_rms?: number
  report_interval?: number
  ota_interval?: number
}

export async function publishDeviceConfig(
  id: string,
  input: DeviceConfigInput,
  config?: AxiosRequestConfig
): Promise<void> {
  await api.put(`/devices/${id}/config`, input, config)
}

export async function getDeviceMessages(
  name: string,
  config?: AxiosRequestConfig
): Promise<DeviceMessage[]> {
  const response = await api.get(`/devices/${name}/messages`, config)

  return response.data
}

export async function getDeviceMidSensors(
  name: string,
  params: MidSensorQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
): Promise<MidSensor[]> {
  const response = await api.get<MidSensor[]>(`/devices/${name}/mid-sensors`, {
    ...config,
    params
  })

  return response.data
}

export async function getDeviceMidSensorAggregates(
  name: string,
  params: MidSensorAggregateQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
): Promise<MidSensorAggregate[]> {
  const response = await api.get<MidSensorAggregate[]>(`/devices/${name}/mid-sensor-aggregates`, {
    ...config,
    params
  })

  return response.data
}

export async function getSensorMetas(
  id: string,
  query: SensorMetaQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<SensorMeta[]>(`/devices/${id}/sensor-metas`, {
    params: query,
    ...config
  })

  return response.data
}

export async function getSensorMetaByTime(
  id: string,
  time: Date,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<SensorMeta>(`/devices/${id}/sensor-metas`, {
    params: { time },
    ...config
  })

  return response.data
}

export async function getDeviceSim(id: string, config?: AxiosRequestConfig): Promise<OnomondoSim> {
  const response = await api.get<OnomondoSim>(`/devices/${id}/sim`, config)

  return response.data
}

export async function activateDeviceSim(
  id: string,
  config?: AxiosRequestConfig
): Promise<OnomondoSim> {
  const response = await api.patch<OnomondoSim>(`/devices/${id}/sim/activate`, null, config)

  return response.data
}

export async function deactivateDeviceSim(
  id: string,
  config?: AxiosRequestConfig
): Promise<OnomondoSim> {
  const response = await api.patch<OnomondoSim>(`/devices/${id}/sim/deactivate`, null, config)

  return response.data
}

export default {
  getDevices,
  getDevice,
  getSensorNow,
  getLastSensor,
  getProjectDevices
}
